import {useMemo, useState} from "react";
import {ActivityPeriodEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import { getFormattedDatabaseCode } from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

export type UseActivityPeriodDataTableFilterProps = Omit<UseDataTableFilterProps<ActivityPeriodEntity>, 'filterConfig'>

export function useActivityPeriodDataTableFilter(props: UseActivityPeriodDataTableFilterProps): UseDataTableFilterResult<ActivityPeriodEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [periodQuery, setPeriodQuery] = useState<string | undefined>(undefined);
  const [periodDebouncedQuery] = useDebouncedValue(periodQuery, 200);

  const [categoryQuery, setCategoryQuery] = useState<string | undefined>(undefined);
  const [categoryDebouncedTypeQuery] = useDebouncedValue(categoryQuery, 200);

  const [statusQuery, setStatusQuery] = useState<string | undefined>(undefined);
  const [statusDebouncedTypeQuery] = useDebouncedValue(statusQuery, 200);

  const [operatorUnitQuery, setOperatorUnitQuery] = useState<string | undefined>(undefined);
  const [operatorUnitDebouncedTypeQuery] = useDebouncedValue(operatorUnitQuery, 200);

  const filterConfig: DataTableFilterConfig<ActivityPeriodEntity> = useMemo(() => {
    return {
      "period": {
        setQuery: setPeriodQuery,
        query: periodQuery,
        debouncedQuery: periodDebouncedQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.type, t)
          return !record.type.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
        }
      },
      "category": {
        setQuery: setCategoryQuery,
        query: categoryQuery,
        debouncedQuery: categoryDebouncedTypeQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.category, t)
          return !record.category.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
        }
      },
      "status": {
        setQuery: setStatusQuery,
        query: statusQuery,
        debouncedQuery: statusDebouncedTypeQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.lowestStatus, t)
          return !record.lowestStatus.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
        }
      },
      "operatorUnit": {
        setQuery: setOperatorUnitQuery,
        query: operatorUnitQuery,
        debouncedQuery: operatorUnitDebouncedTypeQuery,
        filter: (record, query) => !record.operatorUnitName || !record.operatorUnitName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    periodQuery,
    periodDebouncedQuery,
    categoryQuery,
    categoryDebouncedTypeQuery,
    statusQuery,
    statusDebouncedTypeQuery,
    operatorUnitQuery,
    operatorUnitDebouncedTypeQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
